<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import FormattedContent from 'dashboard/components-next/message/bubbles/Text/FormattedContent.vue';

const props = defineProps({
  actions: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const store = useStore();
const teams = computed(() => store.getters['teams/getTeams'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);

const actionTranslations = {
  send_message: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.SEND_MESSAGE'),
  add_label: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.ADD_LABEL'),
  assign_team: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.ASSIGN_TEAM'),
  assign_agent: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.ASSIGN_AGENT'),
  mute_conversation: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.MUTE_CONVERSATION'),
  change_status: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.CHANGE_STATUS'),
  remove_label: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.REMOVE_LABEL'),
  remove_assigned_agent: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.REMOVE_ASSIGNED_AGENT'),
  remove_assigned_team: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.REMOVE_ASSIGNED_TEAM'),
  resolve_conversation: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.RESOLVE_CONVERSATION'),
  snooze_conversation: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.SNOOZE_CONVERSATION'),
  change_priority: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.CHANGE_PRIORITY'),
  send_email_transcript: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.SEND_EMAIL_TRANSCRIPT'),
  send_attachment: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.SEND_ATTACHMENT'),
  add_private_note: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.ADD_PRIVATE_NOTE'),
  send_webhook_event: () =>
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.SEND_WEBHOOK_EVENT'),
};

const actionLabel = action => {
  if (action.kind === 'template') {
    return t('CONVERSATION.SCHEDULED_WORKFLOWS.TEMPLATE');
  }
  return (
    actionTranslations[action.name]?.() ||
    t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ACTIONS.UNKNOWN')
  );
};

const recordName = (records, id) =>
  records.find(record => record.id === Number(id))?.name || `#${id}`;

const assignmentValue = (kind, id) => {
  if (['nil', '0'].includes(String(id))) {
    return t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.UNASSIGNED');
  }
  if (id === 'self') {
    return t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.CURRENT_AGENT');
  }
  if (id === 'last_responding_agent') {
    return t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.LAST_RESPONDING_AGENT');
  }
  return kind === 'team'
    ? recordName(teams.value, id)
    : recordName(agents.value, id);
};

const priorityValue = priority =>
  ({
    nil: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.PRIORITY.NONE'),
    low: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.PRIORITY.LOW'),
    medium: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.PRIORITY.MEDIUM'),
    high: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.PRIORITY.HIGH'),
    urgent: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.PRIORITY.URGENT'),
  })[priority] || priority;

const statusValue = status =>
  ({
    open: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.STATUS.OPEN'),
    pending: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.STATUS.PENDING'),
    resolved: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.STATUS.RESOLVED'),
    snoozed: t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.STATUS.SNOOZED'),
  })[status] || status;

const actionValue = action => {
  if (action.kind === 'template') {
    return [action.template?.name, action.template?.language]
      .filter(Boolean)
      .join(' · ');
  }

  const params = Array(action.params || []);
  const resolvers = {
    assign_team: () => assignmentValue('team', params[0]),
    assign_agent: () => assignmentValue('agent', params[0]),
    change_priority: () => priorityValue(params[0]),
    change_status: () => statusValue(params[0]),
  };
  return resolvers[action.name]?.() || params.join(', ');
};

const attachmentLabel = attachment =>
  attachment.filename ||
  t('CONVERSATION.SCHEDULED_WORKFLOWS.PREVIEW.ATTACHMENTS', {
    count: 1,
  });
</script>

<template>
  <ol class="m-0 flex list-none flex-col gap-2 p-0">
    <li
      v-for="(action, index) in props.actions"
      :key="`${action.kind}-${action.name}-${index}`"
      class="flex gap-2 rounded-lg border border-n-amber-5 bg-n-alpha-1 p-3"
    >
      <span
        class="flex size-6 shrink-0 items-center justify-center rounded-full bg-n-amber-4 text-xs font-semibold text-n-amber-12"
      >
        {{ index + 1 }}
      </span>
      <div class="min-w-0 flex-1">
        <p class="m-0 text-xs font-semibold uppercase text-n-amber-11">
          {{ actionLabel(action) }}
        </p>
        <p
          v-if="actionValue(action)"
          class="mb-0 mt-1 break-words text-sm font-medium text-n-amber-12"
        >
          {{ actionValue(action) }}
        </p>
        <FormattedContent
          v-if="action.content"
          :content="action.content"
          class="mt-1 text-sm text-n-amber-12"
        />

        <div
          v-if="action.attachments?.length"
          class="mt-2 flex flex-wrap gap-2"
        >
          <template
            v-for="attachment in action.attachments"
            :key="attachment.id || attachment.url"
          >
            <img
              v-if="attachment.file_type === 'image'"
              :src="attachment.url"
              :alt="attachmentLabel(attachment)"
              class="size-[72px] rounded-xl object-cover"
            />
            <video
              v-else-if="attachment.file_type === 'video'"
              :src="attachment.url"
              class="size-[72px] rounded-xl object-cover"
              controls
              preload="metadata"
            />
            <audio
              v-else-if="attachment.file_type === 'audio'"
              :src="attachment.url"
              class="h-9 max-w-full"
              controls
              preload="metadata"
            />
            <a
              v-else
              :href="attachment.url"
              class="flex h-9 max-w-full items-center gap-2 rounded-lg border border-n-amber-5 bg-n-alpha-white px-3 text-sm text-n-amber-12"
              rel="noreferrer noopener nofollow"
              target="_blank"
            >
              <span class="i-ph-file size-4 shrink-0" aria-hidden="true" />
              <span class="truncate">{{ attachmentLabel(attachment) }}</span>
            </a>
          </template>
        </div>
      </div>
    </li>
  </ol>
</template>
